import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { map, tap } from "rxjs/operators";
import { PostPlatform } from "../../../../shared/enums/campaignPost.enums";
import {
  BrandCampaignCalendarPost,
  BrandCampaignFacebookCalendarPost,
  BrandCampaignGoogleCalendarPost,
  BrandCampaignLinkedInCalendarPost,
} from "../../../../shared/models/brandCampaignCalendarPost";
import { BrandService } from "../../../../shared/services/api/brand.service";
import { HttpClient } from "../../../../shared/services/http-client";
import { UnreachableCaseError } from "../../../../../harmony/core";

export interface ICalendarPostAPIParameters {
  post_id?: number;
  text?: string;
  suggested_publish_date?: number;
  will_become_ad?: boolean;
  scheduled_publish_date_timezone?: string;
  is_dark_post?: boolean;
}

@Injectable()
export class BrandCalendarPostService {
  public calendarPostsRefresh$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private brandService: BrandService,
  ) {}

  public addBrandCalendarPost(
    campaignId: number,
    calendarId: number,
    parameters: ICalendarPostAPIParameters,
  ): Observable<BrandCampaignCalendarPost> {
    const brandId = this.brandService.currentBrandId;
    return this.http
      .post(
        `brand/${brandId}/campaign/${campaignId}/calendar/${calendarId}/calendar_post`,
        parameters,
      )
      .pipe(
        map(({ body }) => {
          const platform: PostPlatform = body.platform;

          switch (platform) {
            case PostPlatform.Facebook:
              return new BrandCampaignFacebookCalendarPost(body);
            case PostPlatform.Google:
              return new BrandCampaignGoogleCalendarPost(body);
            case PostPlatform.LinkedIn:
              return new BrandCampaignLinkedInCalendarPost(body);
            default:
              throw new UnreachableCaseError(platform);
          }
        }),
        tap(() => this.calendarPostsRefresh$.next()),
      );
  }

  public updateBrandCalendarPost(
    campaignId: number,
    calendarId: number,
    postId: number,
    parameters: ICalendarPostAPIParameters,
  ): Observable<BrandCampaignCalendarPost> {
    const brandId = this.brandService.currentBrandId;
    return this.http
      .put(
        `brand/${brandId}/campaign/${campaignId}/calendar/${calendarId}/calendar_post/${postId}`,
        parameters,
      )
      .pipe(
        map(({ body }) => {
          const platform: PostPlatform = body.platform;

          switch (platform) {
            case PostPlatform.Facebook:
              return new BrandCampaignFacebookCalendarPost(body);
            case PostPlatform.Google:
              return new BrandCampaignGoogleCalendarPost(body);
            case PostPlatform.LinkedIn:
              return new BrandCampaignLinkedInCalendarPost(body);
            default:
              throw new UnreachableCaseError(platform);
          }
        }),
        tap(() => this.calendarPostsRefresh$.next()),
      );
  }

  public deleteBrandCalendarPost(
    campaignId: number,
    calendarId: number,
    postId: number,
  ): Observable<void> {
    const brandId = this.brandService.currentBrandId;
    return this.http
      .del(
        `brand/${brandId}/campaign/${campaignId}/calendar/${calendarId}/calendar_post/${postId}`,
      )
      .pipe(map(() => {}));
  }
}
